'use client'

import { AlertV2, Button, List, RadioGroup, Stack, TextField, Typography } from '@hermes/web-components'
import { InputAdornment } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'

import { FlowHeader } from '@components/FlowHeader'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { useUserToken } from '@hooks/useUserToken'

import { useUser } from '@lib/contexts/userContext'
import { useCheckoutStore } from '@lib/stores/checkout'

import { AddressAutocompleteForm } from '@components/AddressAutocompleteForm'
import BookDetailsModal from '@components/BookDetailsModal'
import { getImageSource, STATIC_RESOURCE_HOSTS } from '@components/BookImage'
import { BookListItem } from '@components/BookListItem'
import { ListWithButtonAndChevron } from '@components/ListWithChevron'
import { ListingDto } from '@hermes/domains'
import { ExtendedListing } from '@hermes/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { useIntercom } from '@hooks/useIntercom'
import { useLocale } from '@hooks/useLocale'
import { useNavigation } from '@hooks/useNavigation'
import { useFlashMessage } from '@lib/contexts/flashMessageContext'
import { sendMeetupRequest } from '@lib/services/request'
import { createHostedCheckoutUrl, CreateTransactionSessionParamsSchema } from '@lib/services/transactions'
import { changePhoneNumber } from '@lib/services/user'
import { createUserAddress } from '@lib/services/userAddress'
import getRegionFromLocale from '@lib/utils/getRegionFromLocale'
import { zodParse } from '@lib/utils/schemaHelpers'
import InlineLoaderAnimation from '@lib/assets/lottie/inline-loader.json'
import { CountryCode, getCountryCallingCode, parsePhoneNumber } from 'libphonenumber-js'
import Lottie from 'lottie-react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  ChipStyled,
  DividerStyled,
  FormControlLabelStyled,
  FormHelperTextStyled,
  RadioStyled,
  TextArea
} from './CheckoutPage.styles'
import { BodyContainer } from './styles'

type BookCardListProps = {
  cartItems: (ListingDto | ExtendedListing)[]
  onClickListItem: (listing: ListingDto | ExtendedListing) => void
  chipLabel?: string
  onClickSecondaryAction?: (listingUuid: ListingDto['uuid']) => void
  chipOnHover?: boolean
  secondaryActionDisabled: boolean
}

export const isListingDto = (listing: unknown): listing is ListingDto =>
  typeof listing === 'object' && listing !== null && 'schemaId' in listing && listing.schemaId === 'listing_dto'

const BookCardList: React.FC<BookCardListProps> = ({
  cartItems,
  chipLabel,
  chipOnHover,
  onClickListItem,
  onClickSecondaryAction,
  secondaryActionDisabled
}) => {
  const { t, n } = useTranslateMessage()
  const locale = useLocale()
  const region = getRegionFromLocale(locale)

  return (
    <List>
      {cartItems.map((cartItem) => (
        <React.Fragment key={cartItem.uuid}>
          <BookListItem
            primaryText={cartItem.book.title}
            secondaryText={t({ id: cartItem.book.format, defaultMessage: 'Hardcover' })}
            bookImageUrl={
              (isListingDto(cartItem)
                ? getImageSource(cartItem.book.smallImage, region).source
                : cartItem.book.smallImage) || '/img/default-cover.png'
            }
            onClickListItem={() => onClickListItem(cartItem)}
            onClickSecondaryAction={() => onClickSecondaryAction && onClickSecondaryAction(cartItem.uuid)}
            secondaryActionDisabled={secondaryActionDisabled}
            primaryChipLabel={n(cartItem.totalPrice || cartItem.price)}
            secondaryChipLabel={chipLabel}
            chipOnHover={chipOnHover}
          />
        </React.Fragment>
      ))}
    </List>
  )
}

type ActiveForm = {
  recipientName: boolean
  recipientPhone: boolean
  recipientAddress: boolean
  recipientAddressList: boolean
}

type CheckoutForm = {
  recipientFirstName: string
  recipientLastName: string
  recipientPhone: string
  recipientAddress: number
  giftCardCode: string
  messageToSeller: string
}

export const CheckoutPage = () => {
  const { navigate } = useNavigation()
  const locale = useLocale()
  const region = getRegionFromLocale(locale)
  const countryCode = region.toUpperCase() as CountryCode
  const flashMessage = useFlashMessage()
  const { user, isLoading: userLoading, revalidate: revalidateUser } = useUser()
  const { token } = useUserToken()
  const { t } = useTranslateMessage()

  useIntercom({ hideLauncher: true })

  const removeFromCart = useCheckoutStore((state) => state.removeFromCart)
  const addToCart = useCheckoutStore((state) => state.addToCart)
  const cartItems = useCheckoutStore((state) => state.cart.items) as (ListingDto | ExtendedListing)[]
  const setAddress = useCheckoutStore((state) => state.setAddress)
  const setRecipientDetail = useCheckoutStore((state) => state.setRecipientDetail)
  const recipientFirstName = useCheckoutStore((state) => state.fulfillment.recipient.firstName)
  const recipientLastName = useCheckoutStore((state) => state.fulfillment.recipient.lastName)
  const recipientPhone = useCheckoutStore((state) => state.fulfillment.recipient.phone)
  const recipientAddress = useCheckoutStore((state) => state.fulfillment.shippingAddressId)
  const primaryAddress = user?.addresses?.find((address) => address.isPrimary === true)?.id
  const shippingMethod = useCheckoutStore((state) => state.fulfillment.shippingMethod)
  const shippingProvider = useCheckoutStore((state) => state.fulfillment.shippingProvider)

  const isInCart = (listing: ListingDto | ExtendedListing) => cartItems.some((item) => item.uuid === listing.uuid)

  const isMeetupRequest = shippingMethod === 'meetup'

  const cartCheckoutSchema = z.object({
    recipientFirstName: z.string().min(1, {
      message: t({ id: 'validation.message.firstName.required', defaultMessage: 'First name is required' })
    }),
    recipientLastName: z
      .string()
      .min(1, { message: t({ id: 'validation.message.lastName.required', defaultMessage: 'Last name is required' }) }),
    recipientPhone: z.string().refine(
      (val) => {
        try {
          const phoneNumber = parsePhoneNumber(val, countryCode)
          return phoneNumber.isValid()
        } catch {
          return false
        }
      },
      { message: t({ id: 'validation.message.phone.invalid', defaultMessage: 'Invalid phone number' }) }
    ),
    recipientAddress: z.number(),
    giftCardCode: z.string().optional(),
    messageToSeller: z.string().optional()
  })

  const methods = useForm<CheckoutForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    resolver: zodResolver(cartCheckoutSchema)
  })
  const { register, control, getValues, trigger } = methods
  const { errors } = methods.formState

  const dialCode = getCountryCallingCode(countryCode)

  const activeFormFieldsInitialState = {
    recipientName: false,
    recipientPhone: false,
    recipientAddress: false,
    recipientAddressList: false
  }
  const [activeFormFields, setActiveFormFields] = useState<ActiveForm>(activeFormFieldsInitialState)
  const [formSubmitting, setFormSubmitting] = useState(false)

  const [bookDetailsModalShow, setBookDetailsModalShow] = useState(false)
  const [bookDetailsData, setBookDetailsData] = useState<ListingDto | ExtendedListing | null>(null)

  const openBookDetailsModal = (listing: ListingDto | ExtendedListing) => {
    setBookDetailsData(listing)
    setBookDetailsModalShow(true)
  }

  const showSummary = Object.values(activeFormFields).every((value) => value === false)

  const toggleFormVisibility = (form: keyof ActiveForm) => {
    const newActiveFormFields = { ...activeFormFieldsInitialState }
    newActiveFormFields[form] = !activeFormFields[form]
    setActiveFormFields(newActiveFormFields)
  }

  const selectedAddressStreet =
    user?.addresses?.find((address) => Number(address.id) === Number(getValues('recipientAddress'))) ?? null
  const hasAddresses = (user?.addresses && user?.addresses?.length > 0) ?? null

  const listItems = [
    {
      name: 'recipientName',
      fields: ['recipientFirstName', 'recipientLastName'],
      primaryText: t({ id: 'checkout.form.item.fullName', defaultMessage: 'Full Name' }),
      secondaryText: userLoading
        ? t({ id: 'loading', defaultMessage: 'Loading...' })
        : getValues('recipientFirstName') && `${getValues('recipientFirstName')} ${getValues('recipientLastName')}`,
      onClick: () => toggleFormVisibility('recipientName')
    },
    {
      name: 'recipientPhone',
      fields: ['recipientPhone'],
      primaryText: t({ id: 'checkout.form.item.phoneNumber', defaultMessage: 'Phone Number' }),
      secondaryText: userLoading ? t({ id: 'loading', defaultMessage: 'Loading...' }) : getValues('recipientPhone'),
      onClick: () => toggleFormVisibility('recipientPhone')
    },
    {
      name: 'recipientAddress',
      fields: ['recipientAddress'],
      primaryText: t({ id: 'checkout.form.item.address', defaultMessage: 'Address' }),
      secondaryText: userLoading
        ? t({ id: 'loading', defaultMessage: 'Loading...' })
        : selectedAddressStreet && `${selectedAddressStreet.streetAddress} ${selectedAddressStreet.streetNumeric}`,
      onClick: hasAddresses
        ? () => toggleFormVisibility('recipientAddressList')
        : () => toggleFormVisibility('recipientAddress')
    }
  ]

  const userAddresses = user?.addresses?.map((address: any) => ({
    id: address.id,
    title: `${address.streetAddress} ${address.streetNumeric}`,
    description: `${address.zip} ${address.city}`,
    chipLabel: address.primary && t({ id: 'addressList.primary.label', defaultMessage: 'Primary' })
  }))

  const handleCreateAddress = useCallback(
    async (address: any) => {
      try {
        const newAddress = await createUserAddress({
          formData: address,
          country: region,
          token
        })
        flashMessage.addFlashMessage(
          t({ id: 'addressForm.submit.successMessage', defaultMessage: 'Saved new address' }),
          'success'
        )
        revalidateUser()
        setAddress(newAddress.id)
        toggleFormVisibility('recipientAddressList')
      } catch (error) {
        flashMessage.addFlashMessage(
          t({ id: 'addressForm.submit.errorMessage', defaultMessage: 'Failed to create new address' }),
          'error'
        )
      }
    },
    [token, revalidateUser, setAddress, flashMessage]
  )

  const handleSaveAddress = (e: React.ChangeEvent<HTMLInputElement>) => {
    trigger('recipientAddress')
    if (errors.recipientAddress) return
    methods.setValue('recipientAddress', Number(e.target.value))
    setAddress(Number(e.target.value))
  }

  const handleSaveName = () => {
    trigger('recipientFirstName')
    trigger('recipientLastName')
    if (errors.recipientFirstName) return
    if (errors.recipientLastName) return
    if (!methods.getValues('recipientFirstName') || !methods.getValues('recipientLastName')) return
    const fullName = `${methods.getValues('recipientFirstName')} ${methods.getValues('recipientLastName')}`
    setRecipientDetail({
      fullName,
      firstName: methods.getValues('recipientFirstName'),
      lastName: methods.getValues('recipientLastName')
    })
    toggleFormVisibility('recipientName')
  }

  const handleSavePhone = () => {
    trigger('recipientPhone')
    if (errors.recipientPhone) return
    const rawPhoneNumber = methods.getValues('recipientPhone')
    if (!rawPhoneNumber) return
    const phoneNumber = parsePhoneNumber(rawPhoneNumber, countryCode)
    if (phoneNumber && phoneNumber.isValid()) {
      setRecipientDetail({
        phone: phoneNumber.number
      })
    }
    if (user && user.phone !== phoneNumber.number) {
      changePhoneNumber({ token, phoneNumber: phoneNumber.number, userId: user.id, country: region }).catch(() => {
        flashMessage.addFlashMessage(
          t({ id: 'phoneForm.submit.errorMessage', defaultMessage: 'Failed to change phone number' }),
          'error'
        )
      })
    }
    toggleFormVisibility('recipientPhone')
  }

  const handleNavBarNavigate = () => {
    const NAVIGATION_STATES = {
      recipientName: () => toggleFormVisibility('recipientName'),
      recipientPhone: () => toggleFormVisibility('recipientPhone'),
      recipientAddressList: () => toggleFormVisibility('recipientAddressList'),
      recipientAddress: () => toggleFormVisibility('recipientAddress')
    }
    const activeViewKey = Object.keys(activeFormFields).find(
      (key) => activeFormFields[key as keyof ActiveForm] === true
    )
    const sellerUuid = cartItems.length > 0 && cartItems[0].seller.uuid
    if (sellerUuid && !activeViewKey) {
      return () => navigate.moreFromSellerPage(sellerUuid)
    }
    return NAVIGATION_STATES[activeViewKey as keyof typeof NAVIGATION_STATES]
  }

  const handleSubmitCheckoutShipping: SubmitHandler<CheckoutForm> = async (data) => {
    setFormSubmitting(true)
    try {
      const promoCode = data.giftCardCode ?? null
      const products = cartItems.map((item) => ({
        saleId: item.id,
        title: item.book.title,
        thumbnailUrl: new URL(
          item.book.smallImage ?? '/img/default-cover.png',
          STATIC_RESOURCE_HOSTS[region]
        ).toString()
      }))
      const rawBody = {
        userId: user?.id,
        checkoutProvider: 'stripe',
        shippingProvider,
        shippingAddressId: data.recipientAddress,
        products,
        royaltyDonationAmount: 0,
        cancelRedirectUrl: `${window.location.href}?canceled=true`,
        successRedirectUrl: `${window.location.origin}/${locale}/request/confirmation`,
        ...(promoCode && { promotionCode: promoCode })
      }
      const body = zodParse(rawBody, CreateTransactionSessionParamsSchema)
      if (!body) {
        throw new Error(t({ id: 'checkout.submit.errorMessage', defaultMessage: 'Failed to open checkout' }))
      }

      const res = await createHostedCheckoutUrl({
        body,
        country: region,
        token
      })
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl
      }
    } catch (err: any) {
      flashMessage.addFlashMessage(err.message, 'error')
    } finally {
      setFormSubmitting(false)
    }
  }

  const handleSubmitCheckoutMeetup: SubmitHandler<CheckoutForm> = async (data) => {
    setFormSubmitting(true)
    if (token && user) {
      try {
        const requestPromises = cartItems.map((item, idx) =>
          sendMeetupRequest({
            userId: user?.id,
            price: item.price,
            saleId: item.id,
            sellerId: item.seller.id,
            country: region,
            message: idx === 0 ? data.messageToSeller : ' ',
            token
          })
        )
        await Promise.all(requestPromises)
        navigate.checkoutConfirmationPage('meetup', null)
      } catch (error: any) {
        flashMessage.addFlashMessage(error.message, 'error')
      } finally {
        setFormSubmitting(false)
      }
    }
  }

  const handleSubmitCheckout: SubmitHandler<CheckoutForm> = (data) => {
    if (isMeetupRequest) {
      return handleSubmitCheckoutMeetup(data)
    }
    return handleSubmitCheckoutShipping(data)
  }

  const navBarTitle = () => {
    const NAVBAR_TITLE = {
      recipientName: t({ id: 'flowHeader.title:checkout.recipientName', defaultMessage: 'Recipient Name' }),
      recipientPhone: t({ id: 'flowHeader.title:checkout.recipientPhone', defaultMessage: 'Recipient Phone' }),
      recipientAddress: t({ id: 'flowHeader.title:checkout.recipientAddressList', defaultMessage: 'Recipient Address' })
    }
    const activeViewKey = Object.keys(activeFormFields).find(
      (key) => activeFormFields[key as keyof ActiveForm] === true
    )
    if (!activeViewKey) return t({ id: 'flowHeader.title:checkout.summary', defaultMessage: 'Summary' })
    return NAVBAR_TITLE[activeViewKey as keyof typeof NAVBAR_TITLE]
  }

  useEffect(() => {
    if (!userLoading && user) {
      const initialState = {
        recipientFirstName: recipientFirstName || user?.firstName || '',
        recipientLastName: recipientLastName || user?.lastName || '',
        recipientPhone:
          (recipientPhone && parsePhoneNumber(recipientPhone, countryCode).nationalNumber) ||
          (user?.phone && parsePhoneNumber(user?.phone || '', countryCode).nationalNumber) ||
          '',
        recipientAddress: recipientAddress || primaryAddress || 0
      }
      methods.reset(initialState)
    }
  }, [user])

  return (
    <>
      <FlowHeader title={navBarTitle()} onClick={handleNavBarNavigate()} />
      <BodyContainer>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmitCheckout)}>
            {showSummary && (
              <>
                <Stack spacing={3}>
                  <BookCardList
                    cartItems={cartItems}
                    onClickSecondaryAction={removeFromCart}
                    onClickListItem={openBookDetailsModal}
                    secondaryActionDisabled={cartItems.length === 1}
                    chipOnHover
                  />
                  {bookDetailsData && (
                    <BookDetailsModal
                      isOpen={bookDetailsModalShow}
                      onClose={() => setBookDetailsModalShow(false)}
                      listing={bookDetailsData}
                      actionLabel={
                        isInCart(bookDetailsData)
                          ? t({ id: 'remove', defaultMessage: 'Remove' })
                          : t({ id: 'add', defaultMessage: 'Add' })
                      }
                      actionDisabled={cartItems.length === 1}
                      action={() => {
                        if (isInCart(bookDetailsData)) {
                          removeFromCart(bookDetailsData.uuid)
                          return
                        }
                        addToCart(bookDetailsData)
                      }}
                    />
                  )}

                  <ListWithButtonAndChevron listItems={listItems} errors={errors} />

                  {isMeetupRequest ? (
                    <>
                      <Typography variant="h4">
                        {t({ id: 'checkout.messageToSeller.heading', defaultMessage: 'Message to seller' })}
                      </Typography>
                      <TextArea {...register('messageToSeller')} placeholder="Your message to the seller" minRows={3} />
                      <AlertV2
                        severity="info"
                        sx={{
                          backgroundColor: 'neutral.100',
                          padding: '12px',
                          borderRadius: '8px',
                          typography: 'body1'
                        }}
                      >
                        {t({
                          id: 'checkout.summary.infoMessage.meetup',
                          defaultMessage:
                            'This is a meetup request. Please let the seller know how you would like to handle payment and delivery before sending the request.'
                        })}
                      </AlertV2>

                      <Button type="submit" variant="contained" disabled={formSubmitting}>
                        {formSubmitting ? (
                          <Lottie animationData={InlineLoaderAnimation} loop style={{ width: 50, height: 50 }} />
                        ) : (
                          t({ id: 'checkout.sendRequest.meetup', defaultMessage: 'Send request' })
                        )}
                      </Button>
                    </>
                  ) : (
                    <>
                      <TextField
                        {...register('giftCardCode')}
                        label={t({
                          id: 'checkout.form.giftCardCode',
                          defaultMessage: '🎁 Have a gift card? Enter your code'
                        })}
                      />

                      <AlertV2
                        severity="info"
                        sx={{
                          backgroundColor: 'neutral.100',
                          padding: '12px',
                          borderRadius: '8px',
                          typography: 'body1'
                        }}
                      >
                        {t({
                          id: 'checkout.summary.infoMessage.payment',
                          defaultMessage:
                            'By clicking next you will transferred to our payment provider to complete the checkout'
                        })}
                      </AlertV2>

                      <Button type="submit" variant="contained" disabled={formSubmitting}>
                        {formSubmitting ? (
                          <Lottie animationData={InlineLoaderAnimation} loop style={{ width: 50, height: 50 }} />
                        ) : (
                          t({ id: 'buttonNextStep', defaultMessage: 'Next step' })
                        )}
                      </Button>
                    </>
                  )}
                </Stack>
              </>
            )}
            {activeFormFields.recipientName && (
              <>
                <Stack spacing={2}>
                  <TextField
                    {...register('recipientFirstName')}
                    label={t({ id: 'checkout.form.firstName', defaultMessage: 'First Name' })}
                    error={!!errors.recipientFirstName}
                    helperText={errors.recipientFirstName?.message as string}
                  />
                  <TextField
                    {...register('recipientLastName')}
                    label={t({ id: 'checkout.form.lastName', defaultMessage: 'Last Name' })}
                    error={!!errors.recipientLastName}
                    helperText={errors.recipientLastName?.message as string}
                  />
                  <Button onClick={handleSaveName} type="submit" variant="contained">
                    {t({ id: 'button.save', defaultMessage: 'Save' })}
                  </Button>
                </Stack>
              </>
            )}
            {activeFormFields.recipientPhone && (
              <>
                <Stack spacing={2}>
                  <TextField
                    {...register('recipientPhone')}
                    label="Phone Number"
                    type="tel"
                    error={!!errors.recipientPhone}
                    helperText={errors.recipientPhone?.message as string}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+{dialCode}</InputAdornment>
                    }}
                    sx={{
                      '& .MuiInputAdornment-root': {
                        paddingTop: '14px'
                      }
                    }}
                  />{' '}
                  <Button onClick={handleSavePhone} type="submit" variant="contained">
                    {t({ id: 'button.save', defaultMessage: 'Save' })}
                  </Button>
                </Stack>
              </>
            )}
            {userAddresses && activeFormFields.recipientAddressList && (
              <>
                <Controller
                  control={control}
                  name={'recipientAddress'}
                  render={({ field }) => (
                    <RadioGroup {...field} onChange={handleSaveAddress}>
                      {userAddresses.map((item) => (
                        <React.Fragment key={item.id}>
                          <FormControlLabelStyled
                            value={item.id}
                            control={<RadioStyled />}
                            label={
                              <>
                                <Stack direction="row" spacing={2}>
                                  <div>{item.title}</div>
                                  {item.chipLabel && <ChipStyled variant="outlined" label={item.chipLabel} />}
                                </Stack>
                                <FormHelperTextStyled>{item.description}</FormHelperTextStyled>
                              </>
                            }
                          />
                          <DividerStyled />
                        </React.Fragment>
                      ))}
                    </RadioGroup>
                  )}
                />
                <Stack direction={'row'} spacing={2}>
                  <Button variant="contained" onClick={() => toggleFormVisibility('recipientAddressList')}>
                    {t({ id: 'button.save', defaultMessage: 'Save' })}
                  </Button>
                  <Button variant="outlined" onClick={() => toggleFormVisibility('recipientAddress')}>
                    {t({ id: 'button.addNewAddress', defaultMessage: 'Add new address' })}
                  </Button>
                </Stack>
              </>
            )}
          </form>
        </FormProvider>
        {activeFormFields.recipientAddress && (
          <>
            <AddressAutocompleteForm
              onSubmit={handleCreateAddress}
              onClose={() => toggleFormVisibility('recipientAddress')}
            />
          </>
        )}
      </BodyContainer>
    </>
  )
}
